<template>
    <b-modal :id="modalId" size="xl" scrollable :title="editing ? 'Modifier une dépense et sa répartition sur les lots' : 'Créer une dépense et sa répartition sur les lots'" @show="synchronizeLots" @shown="onShown" @hidden="onHidden">
        <section class="od-section">
            <h3 class="od-section-title"><span class="od-section-number">1</span> Informations de la dépense</h3>

            <div class="row align-items-start">
                <div class="col-lg-6">
                    <!-- Recherche dans les bordereaux de prix uniquement : un élément choisi remplit le libellé, le PR et le PV -->
                    <div class="form-group od-designation" ref="designationGroup">
                        <label class="required" :for="modalId + '-designation'">Libellé</label>
                        <input type="text" class="form-control" :class="{'is-invalid': checked && designation.trim() === ''}" :id="modalId + '-designation'" ref="designationInput" v-model="designation" placeholder="Saisissez un libellé ou recherchez un élément de bordereau de prix" autocomplete="off" @input="suggestionSource = null" @keydown.enter.prevent>
                        <div class="invalid-feedback">Le libellé est obligatoire.</div>
                        <small class="form-text text-muted" v-if="suggestionSource"><i class="far fa-tag"></i> {{ suggestionSource }}</small>
                    </div>
                </div>
                <!-- Catégories des prévisions de l'étude : la sous-catégorie est obligatoire pour Achat -->
                <div :class="requiresTypeSecondary ? 'col-lg-3' : 'col-lg-6'">
                    <div class="form-group">
                        <label class="required" :for="modalId + '-type'">Catégorie</label>
                        <select class="form-control" :class="{'is-invalid': checked && !type}" :id="modalId + '-type'" v-model="type" @change="onTypeChange">
                            <option value="">Choisir une catégorie</option>
                            <option v-for="item in categoryOptions" :key="'type-' + item.value" :value="String(item.value)">{{ item.label }}</option>
                        </select>
                        <div class="invalid-feedback">La catégorie est obligatoire.</div>
                    </div>
                </div>
                <div class="col-lg-3" v-if="requiresTypeSecondary">
                    <div class="form-group">
                        <label class="required" :for="modalId + '-type-secondary'">Sous-catégorie</label>
                        <select class="form-control" :class="{'is-invalid': checked && !typeSecondary}" :id="modalId + '-type-secondary'" v-model="typeSecondary">
                            <option value="">Choisir une sous-catégorie</option>
                            <option v-for="item in subcategoryOptions" :key="'type-secondary-' + item.value" :value="String(item.value)">{{ item.label }}</option>
                        </select>
                        <div class="invalid-feedback">La sous-catégorie est obligatoire pour une dépense Achat.</div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-lg-4">
                    <div class="form-group mb-lg-0">
                        <label class="required" :for="modalId + '-cost-amount'">Prix de revient (PR) de la dépense HT</label>
                        <div class="input-group">
                            <input type="number" step="0.01" min="0" class="form-control text-right" :class="{'is-invalid': costAmountErrors.length > 0}" :id="modalId + '-cost-amount'" v-model="costAmount" @input="suggestionSource = null" @blur="computeFrom('amount')" @keydown.enter.prevent>
                            <div class="input-group-append">
                                <span class="input-group-text">€ HT</span>
                            </div>
                            <div class="invalid-feedback">
                                <div v-for="message in costAmountErrors" :key="message">{{ message }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group mb-lg-0">
                        <label :for="modalId + '-margin'">{{ marginType == 2 ? 'Taux de marque' : 'Taux de marge' }}</label>
                        <div class="input-group">
                            <input type="number" step="0.01" min="0" class="form-control text-center" :id="modalId + '-margin'" v-model="margin" @input="marginEdited = true; suggestionSource = null" @blur="computeFrom('margin')" @keydown.enter.prevent>
                            <div class="input-group-append">
                                <span class="input-group-text">%</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-lg-4">
                    <div class="form-group mb-0">
                        <label class="required" :for="modalId + '-selling-amount'">Prix de vente (PV) de la dépense HT</label>
                        <div class="input-group">
                            <input type="number" step="0.01" min="0" class="form-control text-right" :class="{'is-invalid': sellingAmountErrors.length > 0}" :id="modalId + '-selling-amount'" v-model="sellingAmount" @input="marginEdited = true; suggestionSource = null" @blur="computeFrom('selling')" @keydown.enter.prevent>
                            <div class="input-group-append">
                                <span class="input-group-text">€ HT</span>
                            </div>
                            <div class="invalid-feedback">
                                <div v-for="message in sellingAmountErrors" :key="message">{{ message }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section class="od-section">
            <h3 class="od-section-title">
                <span class="od-section-number">2</span> Lots concernés
                <span class="od-section-counter">{{ payload.lots.length }} sélectionné{{ payload.lots.length > 1 ? 's' : '' }} sur {{ lots.length }}</span>
            </h3>

            <!-- Mode de répartition : cartes entièrement cliquables, choix unique -->
            <div class="od-mode" role="radiogroup" aria-label="Mode de répartition">
                <button type="button" role="radio" class="od-mode-card" :class="{'is-active': mode === 'quantity'}" :aria-checked="mode === 'quantity' ? 'true' : 'false'" @click="setMode('quantity')">
                    <span class="od-mode-icon" aria-hidden="true">
                        <!-- Lucide « chart-no-axes-column-increasing » -->
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <line x1="6" x2="6" y1="20" y2="16"></line>
                            <line x1="12" x2="12" y1="20" y2="10"></line>
                            <line x1="18" x2="18" y1="20" y2="4"></line>
                        </svg>
                    </span>
                    <span class="od-mode-text">
                        <span class="od-mode-title">Par quantité</span>
                        <span class="od-mode-description">Répartition automatique selon les quantités des lots sélectionnés.</span>
                    </span>
                    <span class="od-mode-check" aria-hidden="true"><i class="fas fa-check"></i></span>
                </button>
                <button type="button" role="radio" class="od-mode-card" :class="{'is-active': mode === 'percentage'}" :aria-checked="mode === 'percentage' ? 'true' : 'false'" @click="setMode('percentage')">
                    <span class="od-mode-icon" aria-hidden="true"><i class="far fa-percent"></i></span>
                    <span class="od-mode-text">
                        <span class="od-mode-title">Par pourcentage</span>
                        <span class="od-mode-description">Définissez manuellement la part de chaque lot.</span>
                    </span>
                    <span class="od-mode-check" aria-hidden="true"><i class="fas fa-check"></i></span>
                </button>
            </div>

            <div class="ix-table-box mb-0">
                <table class="ix-table od-lots">
                    <thead>
                        <tr>
                            <th style="width: 40px;"></th>
                            <th>Lot</th>
                            <th style="width: 150px;" v-if="mode === 'percentage'">Pourcentage</th>
                            <template v-else>
                                <th style="width: 190px;">Quantité du lot</th>
                                <th style="width: 170px;">Unité</th>
                            </template>
                            <th class="text-right" style="width: 130px;">PR HT réparti</th>
                            <th class="text-right" style="width: 130px;">PV HT réparti</th>
                        </tr>
                    </thead>
                    <tbody>
                        <template v-for="lot in lots">
                            <tr :key="lotKey(lot)" v-if="entries[lotKey(lot)]" :class="entries[lotKey(lot)].selected ? 'is-selected' : 'is-unselected'">
                                <td class="text-center">
                                    <input type="checkbox" :id="modalId + '-' + lotKey(lot)" v-model="entries[lotKey(lot)].selected">
                                </td>
                                <td>
                                    <label class="od-lot-name" :for="modalId + '-' + lotKey(lot)">{{ lot.name || 'Lot sans nom' }}</label>
                                    <span class="od-tag" v-if="!lot.id">Nouveau lot</span>
                                </td>
                                <td v-if="mode === 'percentage'">
                                    <div class="input-group input-group-sm flex-nowrap" v-if="entries[lotKey(lot)].selected">
                                        <input type="number" step="0.01" min="0" max="100" class="form-control text-right" :class="{'is-invalid': percentageIssue(lot) !== null}" :aria-label="'Pourcentage du lot ' + (lot.name || 'sans nom')" v-model="entries[lotKey(lot)].percentage" @input="percentagesEdited = true" @keydown.enter.prevent>
                                        <div class="input-group-append">
                                            <span class="input-group-text">%</span>
                                        </div>
                                    </div>
                                    <span class="ix-text-secondary" v-else>—</span>
                                    <small class="d-block mt-1 ix-text-red" v-if="percentageIssue(lot)">{{ percentageIssue(lot) }}</small>
                                </td>
                                <template v-else>
                                <td>
                                    <div class="input-group input-group-sm flex-nowrap">
                                        <input type="text" class="form-control" :class="{'is-invalid': hasLotError(lot, quantityErrorCodes)}" :value="lotQuantityLabel(lot)" disabled>
                                        <div class="input-group-append" v-if="quantityNeedsInput(lot)">
                                            <button type="button" class="btn od-complete" title="Compléter la quantité dans la synthèse du lot" @click="fillLot(lot, 'quantity')"><i class="far fa-pen"></i> Compléter</button>
                                        </div>
                                    </div>
                                    <small class="d-block mt-1 ix-text-orange" v-if="quantityNeedsInput(lot)">
                                        <i class="far fa-exclamation-triangle"></i> {{ isNumber(lot.quantity) ? 'Quantité invalide' : 'Quantité non renseignée' }}
                                    </small>
                                </td>
                                <td>
                                    <div class="input-group input-group-sm flex-nowrap">
                                        <input type="text" class="form-control" :class="{'is-invalid': hasLotError(lot, unitErrorCodes)}" :value="lotUnit(lot).trim() || '—'" disabled>
                                        <div class="input-group-append" v-if="!lotUnit(lot).trim()">
                                            <button type="button" class="btn od-complete" title="Compléter l'unité dans la synthèse du lot" @click="fillLot(lot, 'unit')"><i class="far fa-pen"></i> Compléter</button>
                                        </div>
                                    </div>
                                    <small class="d-block mt-1 ix-text-orange" v-if="!lotUnit(lot).trim()">
                                        <i class="far fa-exclamation-triangle"></i> Unité non renseignée
                                    </small>
                                </td>
                                </template>
                                <td class="text-right text-nowrap od-amount" :class="{'ix-text-secondary': !sharesAvailable}">{{ lotShare(lot, 'amount') }}</td>
                                <td class="text-right text-nowrap od-amount" :class="{'ix-text-secondary': !sharesAvailable}">{{ lotShare(lot, 'selling') }}</td>
                            </tr>
                            <tr :key="lotKey(lot) + '-details'" v-if="entries[lotKey(lot)] && ((entries[lotKey(lot)].selected && lotChangedSinceSave(lot)) || lotErrors(lot).length > 0 || hiddenLotFieldsToComplete(lot))" class="od-lot-details" :class="entries[lotKey(lot)].selected ? 'is-selected' : 'is-unselected'">
                                <td></td>
                                <td :colspan="mode === 'percentage' ? 4 : 5">
                                    <div class="ix-text-secondary ix-text-small" v-if="entries[lotKey(lot)].selected && lotChangedSinceSave(lot)">
                                        <i class="far fa-history"></i> La quantité ou l'unité de ce lot a été modifiée. Elle sera enregistrée avec la dépense.
                                    </div>
                                    <div class="ix-text-small ix-text-red" v-for="message in lotErrors(lot)" :key="message">
                                        <i class="fas fa-exclamation-circle"></i> {{ message }}
                                    </div>
                                    <div class="ix-text-small ix-text-orange mt-1" v-if="hiddenLotFieldsToComplete(lot)">
                                        <template v-if="lotErrors(lot).length === 0"><i class="far fa-exclamation-triangle"></i> Ce lot doit avoir une quantité et une unité.</template>
                                        <button type="button" class="btn btn-sm od-complete ml-1" v-if="quantityNeedsInput(lot)" @click="fillLot(lot, 'quantity')"><i class="far fa-pen"></i> Compléter la quantité</button>
                                        <button type="button" class="btn btn-sm od-complete ml-1" v-if="!lotUnit(lot).trim()" @click="fillLot(lot, 'unit')"><i class="far fa-pen"></i> Compléter l'unité</button>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </tbody>
                    <tfoot v-if="selectedLots.length > 0">
                        <tr class="od-total">
                            <td></td>
                            <td>Total</td>
                            <td v-if="mode === 'percentage'"></td>
                            <template v-else>
                                <td></td>
                                <td></td>
                            </template>
                            <td class="text-right text-nowrap" :class="{'ix-text-secondary': !sharesAvailable}">{{ totalShare('amount') }}</td>
                            <td class="text-right text-nowrap" :class="{'ix-text-secondary': !sharesAvailable}">{{ totalShare('selling') }}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <div class="od-percentage-total" v-if="mode === 'percentage'">
                <div class="od-balance" :class="percentageTotalValid ? 'is-balanced' : 'is-unbalanced'">
                    <i class="fas" :class="percentageTotalValid ? 'fa-check-circle' : 'fa-exclamation-triangle'"></i>
                    Total de la répartition : {{ formatPercentageValue(percentageTotalCents) }} % / 100 %
                </div>
                <button type="button" class="ix-btn ix-btn-state" @click="splitEqually" :disabled="selectedLots.length === 0">
                    <i class="far fa-equals"></i> Répartir à parts égales
                </button>
            </div>
            <div class="ix-text-small ix-text-red mt-2" v-if="mode === 'percentage' && percentageBlocker">
                <i class="fas fa-exclamation-circle"></i> {{ percentageBlocker }}
            </div>
        </section>

        <div class="alert alert-danger" v-if="requestError">
            <i class="fas fa-exclamation-circle"></i> {{ requestError }}
        </div>

        <div class="alert alert-danger" v-if="isPreviewFresh && (globalErrors.length > 0 || unitGroups.length > 0)">
            <div v-for="message in globalErrors" :key="message"><i class="fas fa-exclamation-circle"></i> {{ message }}</div>
            <div v-if="unitGroups.length > 0">
                <i class="fas fa-exclamation-circle"></i> Les lots sélectionnés doivent avoir la même unité :
                <ul class="mb-0">
                    <li v-for="group in unitGroups" :key="group.unit">{{ group.units }} : {{ group.lots }}</li>
                </ul>
            </div>
        </div>

        <p class="od-hint" v-if="labelOnlyChange">
            <i class="far fa-info-circle"></i>
            Seul le libellé change. La répartition reste identique.
        </p>
        <p class="od-hint" v-else-if="isPreviewFresh && preview.valid && !saveBlocker">
            <i class="far fa-info-circle"></i>
            L'étude sera enregistrée et la dépense répartie entre les lots sélectionnés{{ editing ? ', en remplacement de la répartition actuelle' : '' }}.
        </p>
        <template v-slot:modal-footer>
            <span class="od-footer-hint mr-auto" v-if="footerHint">
                <i class="far" :class="previewPending ? 'fa-circle-notch fa-spin' : 'fa-info-circle'"></i> {{ footerHint }}
            </span>
            <button type="button" class="ix-btn transparent" @click="$bvModal.hide(modalId)">Fermer</button>
            <button type="button" class="ix-btn green" @click="generate" :disabled="loading || saving || saveBlocker !== null">
                <template v-if="saving">
                    <i class="far fa-circle-notch fa-spin"></i> Enregistrement en cours…
                </template>
                <template v-else>
                    <i class="far fa-check"></i> {{ labelOnlyChange ? 'Enregistrer le libellé' : "Enregistrer l'étude et la dépense" }}
                </template>
            </button>
        </template>
    </b-modal>
</template>

<script>
    // Préparation, aperçu et génération d'une dilution : la répartition est entièrement calculée et enregistrée par le serveur
    const GLOBAL_MESSAGES = {
        no_lot_selected: 'Sélectionnez au moins un lot.',
        percentage_total_invalid: 'Le total des pourcentages doit être égal à 100 %.',
    };

    const MODE_QUANTITY = 'quantity';
    const MODE_PERCENTAGE = 'percentage';
    const TYPE_PURCHASE = '2';
    // Délai après la dernière modification avant le calcul automatique de la répartition
    const PREVIEW_DELAY = 500;

    const AMOUNT_MESSAGES = {
        amount_missing: '{label} est obligatoire.',
        amount_invalid: "{label} n'est pas un nombre valide.",
        amount_too_precise: '{label} ne peut pas comporter plus de 2 décimales.',
        amount_negative: '{label} ne peut pas être négatif.',
    };

    const LOT_MESSAGES = {
        quantity_missing: 'Renseignez la quantité de ce lot dans la synthèse du lot.',
        quantity_invalid: "La quantité de ce lot n'est pas valide. Corrigez-la dans la synthèse du lot.",
        quantity_too_precise: 'La quantité de ce lot a trop de décimales (3 au maximum). Corrigez-la dans la synthèse du lot.',
        quantity_zero: 'La quantité de ce lot doit être supérieure à 0. Corrigez-la dans la synthèse du lot.',
        quantity_negative: 'La quantité de ce lot ne peut pas être négative. Corrigez-la dans la synthèse du lot.',
        unit_missing: "Renseignez l'unité de ce lot dans la synthèse du lot.",
        unit_incompatible: "Ce lot n'a pas la même unité que les autres lots sélectionnés. Corrigez-la dans la synthèse du lot.",
        percentage_missing: 'Renseignez un pourcentage.',
        percentage_invalid: "Le pourcentage n'est pas valide.",
        percentage_too_precise: 'Le pourcentage a trop de décimales (2 au maximum).',
        percentage_not_positive: 'Le pourcentage doit être supérieur à 0.',
        percentage_above_maximum: 'Le pourcentage ne peut pas dépasser 100 %.',
    };

    export default {
        name: 'overhead-dilution-modal',
        props: ['lots', 'previewRoute', 'storeRoute', 'suggestionsRoute', 'studyId', 'marginType', 'defaultMarginPurchases', 'defaultMargins', 'predictionTypes', 'purchaseTypes', 'savedLotValues'],
        data() {
            return {
                modalId: 'overhead-dilution-modal-' + Math.random().toString(36).substr(2, 9),
                designation: '',
                costAmount: '',
                margin: '',
                sellingAmount: '',
                marginInitialized: false,
                // Taux saisi ou PV modifié : le taux par défaut de la catégorie ne le remplace plus
                marginEdited: false,
                type: '',
                typeSecondary: '',
                mode: MODE_QUANTITY,
                // Pourcentages saisis : ils ne sont plus recalculés à parts égales quand la sélection change
                percentagesEdited: false,
                entries: {},
                preview: null,
                previewSignature: null,
                // Un aperçu valide a déjà été obtenu : le bouton propose alors de recalculer
                hasValidPreview: false,
                visible: false,
                previewTimer: null,
                // Seule la réponse au dernier calcul demandé est affichée
                previewSequence: 0,
                suggestionSource: null,
                loading: false,
                saving: false,
                editing: null,
                pendingLotIds: null,
                pendingLotPositions: null,
                // Pourcentages à rétablir à l'ouverture : par identifiant de lot (dépense enregistrée) ou par position (saisie en attente)
                pendingPercentagesById: null,
                pendingPercentagesByPosition: null,
                requestError: null,
                checked: false,
                suspended: false,
                fillTarget: null,
                quantityErrorCodes: ['quantity_missing', 'quantity_invalid', 'quantity_too_precise', 'quantity_zero', 'quantity_negative'],
                unitErrorCodes: ['unit_missing', 'unit_incompatible'],
            };
        },
        computed: {
            payload: function() {
                var payload = {
                    amount: this.costAmount,
                    selling: this.sellingAmount,
                    mode: this.mode,
                    lots: [],
                };

                if (this.studyId) {
                    payload.study_id = this.studyId;
                }

                // Rang de création parmi les lots non enregistrés : les nouveaux lots sont toujours ajoutés en fin de liste
                var creationOrder = 0;

                this.lots.forEach(lot => {
                    var order = lot.id ? null : creationOrder++;
                    var entry = this.entries[this.lotKey(lot)];

                    if (!entry || !entry.selected) {
                        return;
                    }

                    var item = {
                        key: this.lotKey(lot),
                        quantity: this.lotQuantity(lot),
                        unit: this.lotUnit(lot),
                    };

                    if (lot.id) {
                        item.id = lot.id;
                    } else {
                        item.creation_order = order;
                    }

                    if (this.mode === MODE_PERCENTAGE) {
                        item.percentage = entry.percentage === null || entry.percentage === undefined ? '' : String(entry.percentage);
                    }

                    payload.lots.push(item);
                });

                return payload;
            },
            categoryOptions: function() {
                return this.predictionTypes && this.predictionTypes.length > 0 ? this.predictionTypes : [{value: 2, label: 'Achat'}];
            },
            subcategoryOptions: function() {
                return this.purchaseTypes || [];
            },
            requiresTypeSecondary: function() {
                return String(this.type) === TYPE_PURCHASE;
            },
            // Sous-catégorie retenue : conservée à l'écran si l'on change de catégorie, mais prise en compte pour Achat seulement
            effectiveTypeSecondary: function() {
                return this.requiresTypeSecondary ? String(this.typeSecondary || '') : '';
            },
            selectedLots: function() {
                return this.lots.filter(lot => this.entries[this.lotKey(lot)] && this.entries[this.lotKey(lot)].selected);
            },
            selectedKeysSignature: function() {
                return this.selectedLots.map(lot => this.lotKey(lot)).join('|');
            },
            // Total en centièmes de pour cent des pourcentages valides des lots sélectionnés
            percentageTotalCents: function() {
                return this.selectedLots.reduce((total, lot) => {
                    var cents = this.percentageIssue(lot) === null ? this.percentageCents(this.entries[this.lotKey(lot)].percentage) : null;

                    return total + (cents === null ? 0 : cents);
                }, 0);
            },
            percentageTotalValid: function() {
                return this.selectedLots.length > 0
                    && this.selectedLots.every(lot => this.percentageIssue(lot) === null)
                    && this.percentageTotalCents === 10000;
            },
            percentageBlocker: function() {
                if (this.mode !== MODE_PERCENTAGE || this.selectedLots.length === 0 || this.percentageTotalValid) {
                    return null;
                }

                if (this.selectedLots.some(lot => this.percentageIssue(lot) !== null)) {
                    return 'Corrigez les pourcentages signalés en rouge.';
                }

                return 'Le total des pourcentages doit être égal à 100 % (actuellement ' + this.formatPercentageValue(this.percentageTotalCents)
                    + ' %). Corrigez-les ou cliquez sur « Répartir à parts égales ».';
            },
            // Seuls le PR, le PV, le mode et les lots envoyés comptent : le libellé n'invalide pas l'aperçu
            isPreviewFresh: function() {
                return this.preview !== null && this.previewSignature === JSON.stringify(this.payload);
            },
            payloadSignature: function() {
                return JSON.stringify(this.payload);
            },
            // Calcul lancé dès que le PR, le PV et au moins un lot sont renseignés
            canPreview: function() {
                return this.isNumber(this.costAmount) && this.isNumber(this.sellingAmount) && this.payload.lots.length > 0;
            },
            previewPending: function() {
                return this.loading || (this.previewTimer !== null && !this.isPreviewFresh);
            },
            // Montants affichés pour un calcul valide, ou indicatifs si seul le total des pourcentages est faux
            sharesAvailable: function() {
                return this.isPreviewFresh && (this.preview.valid || this.preview.provisional === true);
            },
            footerHint: function() {
                if (this.previewPending && this.canPreview) {
                    return 'Calcul de la répartition…';
                }

                return this.saveBlocker;
            },
            previewLotsByKey: function() {
                var lots = {};

                if (this.isPreviewFresh) {
                    this.preview.lots.forEach(lot => {
                        lots[lot.key] = lot;
                    });
                }

                return lots;
            },
            globalErrors: function() {
                if (!this.isPreviewFresh) {
                    return [];
                }

                // Le total des pourcentages est déjà signalé sous le tableau des lots
                return this.preview.errors
                    .filter(code => code !== 'percentage_total_invalid')
                    .map(code => GLOBAL_MESSAGES[code] || code);
            },
            costAmountErrors: function() {
                return this.amountMessages('amount_errors', 'Le prix de revient de la dépense');
            },
            sellingAmountErrors: function() {
                return this.amountMessages('selling_errors', 'Le prix de vente de la dépense');
            },
            unitGroups: function() {
                if (!this.isPreviewFresh || Object.keys(this.preview.units || {}).length < 2) {
                    return [];
                }

                return Object.keys(this.preview.units).map(unit => {
                    var lots = this.lots.filter(lot => this.preview.units[unit].indexOf(this.lotKey(lot)) !== -1);

                    return {
                        unit: unit,
                        units: [...new Set(lots.map(lot => this.lotUnit(lot).trim()))].join(', '),
                        lots: lots.map(lot => lot.name || 'Lot sans nom').join(', '),
                    };
                });
            },
            distributionChanged: function() {
                if (!this.editing) {
                    return true;
                }

                var selected = this.lots.filter(lot => this.entries[this.lotKey(lot)] && this.entries[this.lotKey(lot)].selected);
                if (selected.some(lot => !lot.id || this.lotChangedSinceSave(lot))) {
                    return true;
                }

                var initialSecondary = this.editing.type_secondary === null || this.editing.type_secondary === undefined ? '' : String(this.editing.type_secondary);
                if (this.mode !== (this.editing.mode || MODE_QUANTITY)
                    || String(this.type) !== String(this.editing.type || TYPE_PURCHASE)
                    || this.effectiveTypeSecondary !== initialSecondary) {
                    return true;
                }

                if (this.mode === MODE_PERCENTAGE) {
                    var stored = this.editing.percentages || {};
                    if (selected.some(lot => !this.sameNumber(this.entries[this.lotKey(lot)].percentage, stored[lot.id]))) {
                        return true;
                    }
                }

                var selectedIds = selected.map(lot => Number(lot.id)).sort((a, b) => a - b);
                var initialIds = this.editing.lot_ids.map(id => Number(id)).sort((a, b) => a - b);

                return selectedIds.join(',') !== initialIds.join(',')
                    || !this.sameNumber(this.costAmount, this.editing.amount)
                    || !this.sameNumber(this.margin, this.editing.margin)
                    || !this.sameNumber(this.sellingAmount, this.editing.selling);
            },
            designationChanged: function() {
                return this.editing !== null && this.designation.trim() !== String(this.editing.designation).trim();
            },
            labelOnlyChange: function() {
                return this.editing !== null && !this.distributionChanged && this.designationChanged && this.designation.trim() !== '';
            },
            saveBlocker: function() {
                if (this.editing && !this.distributionChanged) {
                    if (!this.designationChanged) {
                        return 'Aucune modification à enregistrer.';
                    }

                    return this.designation.trim() === '' ? 'Le libellé est obligatoire.' : null;
                }

                return this.generationBlocker;
            },
            generationBlocker: function() {
                if (this.designation.trim() === '') {
                    return 'Le libellé est obligatoire.';
                }

                if (!this.type) {
                    return 'Choisissez la catégorie de la dépense.';
                }

                if (this.requiresTypeSecondary && !this.typeSecondary) {
                    return 'Choisissez une sous-catégorie : elle est obligatoire pour une dépense Achat.';
                }

                if (this.percentageBlocker) {
                    return this.percentageBlocker;
                }

                if (!this.isNumber(this.costAmount) || !this.isNumber(this.sellingAmount)) {
                    return 'Renseignez le PR et le PV de la dépense.';
                }

                if (this.payload.lots.length === 0) {
                    return 'Sélectionnez au moins un lot.';
                }

                if (!this.isNumber(this.margin)) {
                    return (this.marginType == 2 ? 'Le taux de marque' : 'Le taux de marge') + ' est obligatoire.';
                }

                // Le calcul officiel reste le contrôle final avant l'enregistrement
                if (!this.isPreviewFresh) {
                    return 'Calcul de la répartition…';
                }

                if (!this.preview.valid) {
                    return 'Corrigez les erreurs signalées pour enregistrer la dépense.';
                }

                return null;
            },
        },
        watch: {
            payloadSignature: function() {
                this.schedulePreview(false);
            },
            // Parts égales tant que l'utilisateur n'a saisi aucun pourcentage : un lot ajouté ou retiré est réparti automatiquement
            selectedKeysSignature: function() {
                if (this.mode === MODE_PERCENTAGE && !this.percentagesEdited) {
                    this.splitEqually();
                }
            },
        },
        methods: {
            open: function() {
                if (this.suspended) {
                    this.resume();
                    return;
                }

                if (this.editing) {
                    this.reset();
                }

                this.$bvModal.show(this.modalId);
            },
            openForEdit: function(dilution) {
                if (this.suspended && this.editing && this.editing.id == dilution.id) {
                    this.resume();
                    return;
                }

                this.reset();
                this.editing = dilution;
                this.designation = dilution.designation;
                this.costAmount = this.formatInput(dilution.amount);
                this.margin = this.formatInput(dilution.margin);
                this.sellingAmount = this.formatInput(dilution.selling);
                this.marginInitialized = true;
                this.marginEdited = true;
                this.applySettings(dilution.type, dilution.type_secondary, dilution.mode);
                this.pendingLotIds = dilution.lot_ids;
                this.pendingPercentagesById = dilution.mode === MODE_PERCENTAGE ? (dilution.percentages || {}) : null;

                this.$bvModal.show(this.modalId);
            },
            openPending: function(pending, dilution, message) {
                this.reset();
                this.editing = pending.operation === 'update' && dilution ? dilution : null;
                this.designation = pending.designation || '';
                this.costAmount = this.formatInput(pending.amount);
                this.margin = this.formatInput(pending.margin);
                this.sellingAmount = this.formatInput(pending.selling);
                this.marginInitialized = true;
                this.marginEdited = true;
                this.applySettings(pending.type, pending.type_secondary, pending.mode);
                this.pendingLotPositions = (pending.lots || []).map(position => Number(position));
                this.pendingPercentagesByPosition = pending.mode === MODE_PERCENTAGE ? (pending.percentages || {}) : null;
                this.requestError = message;

                this.$bvModal.show(this.modalId);
            },
            lotChangedSinceSave: function(lot) {
                var saved = lot.id && this.savedLotValues ? this.savedLotValues[lot.id] : undefined;
                if (!saved) {
                    return false;
                }

                var sameQuantity = this.isNumber(lot.quantity) || this.isNumber(saved.quantity) ? this.sameNumber(lot.quantity, saved.quantity) : true;

                return !sameQuantity || String(lot.unit || '').trim() !== String(saved.unit || '').trim();
            },
            // Catégorie et mode d'une dépense rouverte : ses pourcentages sont rétablis tels quels, sans nouveau partage égal
            applySettings: function(type, typeSecondary, mode) {
                this.type = type === null || type === undefined || type === '' ? TYPE_PURCHASE : String(type);
                this.typeSecondary = typeSecondary === null || typeSecondary === undefined ? '' : String(typeSecondary);
                this.mode = mode === MODE_PERCENTAGE ? MODE_PERCENTAGE : MODE_QUANTITY;
                this.percentagesEdited = this.mode === MODE_PERCENTAGE;
            },
            defaultMargin: function(type) {
                var margin = this.defaultMargins ? this.defaultMargins[type] : undefined;
                if ((margin === null || margin === undefined) && String(type) === TYPE_PURCHASE) {
                    margin = this.defaultMarginPurchases;
                }

                return margin === null || margin === undefined ? '' : margin;
            },
            // Même règle que les lignes de l'étude : le taux par défaut suit la catégorie tant qu'il n'a pas été saisi
            onTypeChange: function() {
                if (this.editing || this.marginEdited || !this.type) {
                    return;
                }

                this.margin = this.defaultMargin(this.type);
                this.computeFrom('margin');
            },
            setMode: function(mode) {
                if (this.mode === mode) {
                    return;
                }

                this.mode = mode;

                // Pourcentages déjà saisis conservés ; sans aucune saisie, parts égales sur les lots sélectionnés
                if (mode === MODE_PERCENTAGE && !this.selectedLots.some(lot => String(this.entries[this.lotKey(lot)].percentage || '').trim() !== '')) {
                    this.splitEqually();
                }
            },
            // Parts égales au centième, la différence d'arrondi allant aux derniers lots : 33,33 + 33,33 + 33,34
            splitEqually: function() {
                var selected = this.selectedLots;
                if (selected.length === 0) {
                    return;
                }

                var base = Math.floor(10000 / selected.length);
                var remainder = 10000 - base * selected.length;

                selected.forEach((lot, index) => {
                    var cents = base + (index >= selected.length - remainder ? 1 : 0);
                    this.entries[this.lotKey(lot)].percentage = String(cents / 100);
                });

                this.percentagesEdited = false;
            },
            // Centièmes de pour cent, ou null si la valeur n'est pas un nombre positif à 2 décimales au plus
            percentageCents: function(value) {
                var text = value === null || value === undefined ? '' : String(value).trim().replace(',', '.');
                if (!/^\d+(\.\d{1,2})?$/.test(text)) {
                    return null;
                }

                var parts = text.split('.');

                return parseInt(parts[0], 10) * 100 + (parts[1] ? parseInt((parts[1] + '0').substr(0, 2), 10) : 0);
            },
            percentageIssue: function(lot) {
                var entry = this.entries[this.lotKey(lot)];
                if (this.mode !== MODE_PERCENTAGE || !entry || !entry.selected) {
                    return null;
                }

                var text = entry.percentage === null || entry.percentage === undefined ? '' : String(entry.percentage).trim().replace(',', '.');
                if (text === '') {
                    return 'Renseignez un pourcentage.';
                }
                if (isNaN(Number(text))) {
                    return "Le pourcentage n'est pas valide.";
                }
                if (Number(text) < 0) {
                    return 'Le pourcentage ne peut pas être négatif.';
                }

                var cents = this.percentageCents(text);
                if (cents === null) {
                    return 'Le pourcentage a trop de décimales (2 au maximum).';
                }
                if (cents === 0) {
                    return 'Le pourcentage doit être supérieur à 0.';
                }
                if (cents > 10000) {
                    return 'Le pourcentage ne peut pas dépasser 100 %.';
                }

                return null;
            },
            formatPercentageValue: function(cents) {
                return (cents / 100).toLocaleString('fr-FR', {maximumFractionDigits: 2});
            },
            formatInput: function(value) {
                return value === null || value === undefined || value === '' ? '' : String(parseFloat(value));
            },
            lotKey: function(lot) {
                return lot.id ? 'lot-' + lot.id : 'new-' + lot.uniq;
            },
            // Ajoute les lots apparus dans le formulaire et retire ceux qui ont été supprimés, sans toucher aux saisies existantes
            synchronizeLots: function() {
                if (!this.marginInitialized) {
                    // Taux Achat par défaut, comme avant le choix de la catégorie : il suit ensuite la catégorie choisie
                    this.margin = this.defaultMargin(this.type || TYPE_PURCHASE);
                    this.marginInitialized = true;
                }

                var keys = this.lots.map(lot => this.lotKey(lot));

                this.lots.forEach(lot => {
                    var key = this.lotKey(lot);

                    if (!this.entries[key]) {
                        this.$set(this.entries, key, {
                            selected: false,
                            percentage: '',
                        });
                    }
                });

                Object.keys(this.entries).forEach(key => {
                    if (keys.indexOf(key) === -1) {
                        this.$delete(this.entries, key);
                    }
                });

                if (this.pendingLotIds !== null) {
                    var byId = this.pendingPercentagesById || {};
                    this.lots.forEach(lot => {
                        var entry = this.entries[this.lotKey(lot)];
                        entry.selected = lot.id !== null && lot.id !== undefined && this.pendingLotIds.some(id => id == lot.id);
                        if (entry.selected && lot.id && byId[lot.id] !== undefined && byId[lot.id] !== null) {
                            entry.percentage = this.formatInput(byId[lot.id]);
                        }
                    });
                    this.pendingLotIds = null;
                    this.pendingPercentagesById = null;
                }

                if (this.pendingLotPositions !== null) {
                    var byPosition = this.pendingPercentagesByPosition || {};
                    this.lots.forEach((lot, position) => {
                        var entry = this.entries[this.lotKey(lot)];
                        entry.selected = this.pendingLotPositions.indexOf(position) !== -1;
                        if (entry.selected && byPosition[position] !== undefined && byPosition[position] !== null) {
                            entry.percentage = this.formatInput(byPosition[position]);
                        }
                    });
                    this.pendingLotPositions = null;
                    this.pendingPercentagesByPosition = null;
                }
            },
            lotQuantity: function(lot) {
                return lot.quantity === null || lot.quantity === undefined ? '' : String(lot.quantity);
            },
            lotUnit: function(lot) {
                return lot.unit ? String(lot.unit) : '';
            },
            quantityNeedsInput: function(lot) {
                return !this.isNumber(lot.quantity) || parseFloat(lot.quantity) <= 0;
            },
            lotQuantityLabel: function(lot) {
                return this.isNumber(lot.quantity) ? this.formatQuantity(lot.quantity) : '—';
            },
            previewLot: function(lot) {
                return this.previewLotsByKey[this.lotKey(lot)] || null;
            },
            lotErrors: function(lot) {
                var previewLot = this.previewLot(lot);

                // Les pourcentages sont contrôlés et signalés directement sous leur champ
                return previewLot ? previewLot.errors.filter(code => code.indexOf('percentage_') !== 0).map(code => LOT_MESSAGES[code] || code) : [];
            },
            // Mode pourcentage : quantité et unité ne sont plus affichées mais restent obligatoires
            hiddenLotFieldsToComplete: function(lot) {
                var entry = this.entries[this.lotKey(lot)];

                return this.mode === MODE_PERCENTAGE && entry && entry.selected && (this.quantityNeedsInput(lot) || !this.lotUnit(lot).trim());
            },
            lotShare: function(lot, name) {
                var entry = this.entries[this.lotKey(lot)];
                if (!entry || !entry.selected) {
                    return '';
                }

                var previewLot = this.previewLot(lot);
                if (this.sharesAvailable && previewLot && previewLot[name + '_share'] !== null) {
                    return this.currency(previewLot[name + '_share']);
                }

                return this.previewPending && this.canPreview ? '…' : '—';
            },
            totalShare: function(name) {
                if (this.sharesAvailable) {
                    return this.currency(this.preview[name + '_total_share']);
                }

                return this.previewPending && this.canPreview ? '…' : '—';
            },
            // Calcul officiel relancé peu après la dernière modification, jamais à chaque frappe
            schedulePreview: function(immediate) {
                clearTimeout(this.previewTimer);
                this.previewTimer = null;

                if (!this.visible) {
                    return;
                }

                if (!this.canPreview) {
                    this.previewSequence++;
                    this.preview = null;
                    this.previewSignature = null;
                    this.loading = false;
                    return;
                }

                if (this.isPreviewFresh) {
                    return;
                }

                this.previewTimer = setTimeout(() => {
                    this.previewTimer = null;
                    this.recalculate();
                }, immediate ? 0 : PREVIEW_DELAY);
            },
            onShown: function() {
                this.visible = true;
                this.setupSuggestions();
                this.schedulePreview(true);
            },
            // Recherche des lignes d'étude limitée aux bordereaux de prix : même route, mêmes bordereaux, même affichage
            setupSuggestions: function() {
                var input = this.$refs.designationInput;
                if (!input || !this.suggestionsRoute || $(input).data('ui-autocomplete')) {
                    return;
                }

                var vm = this;

                $(input).autocomplete({
                    minLength: 2,
                    // Liste affichée dans la modale, au-dessus de son contenu
                    appendTo: this.$refs.designationGroup,
                    source: (request, response) => {
                        $.ajax({
                            url: this.suggestionsRoute,
                            type: 'get',
                            dataType: 'json',
                            headers: this.getHttpJsonRequestHeaders().headers,
                            // Éléments des bordereaux de prix uniquement, ni articles ni matériel
                            data: {q: request.term, deal_id: $('#deal_id').val(), only: 'prices'},
                            success: (data) => {
                                response($.map(data, (item) => {
                                    return Object.assign(item, {
                                        label: item.designation,
                                        value: item.designation,
                                        formatted_amount: this.currency(item.amount),
                                    });
                                }));
                            },
                        });
                    },
                    create: function() {
                        $(this).data('ui-autocomplete')._renderItem = function(ul, item) {
                            return $('<li>').append(vm.studySuggestionItemHtml(item)).appendTo(ul);
                        };
                    },
                    select: (event, ui) => {
                        this.applySuggestion(ui.item);

                        return false;
                    },
                });
            },
            // Comme une ligne d'étude : PR et PV de l'élément pour une quantité de 1, taux recalculé selon le type de taux de l'étude
            applySuggestion: function(item) {
                this.designation = item.designation || this.designation;
                this.costAmount = this.formatInput(item.amount);
                this.sellingAmount = this.formatInput(item.selling);
                this.marginEdited = true;

                if (this.isNumber(this.sellingAmount)) {
                    this.computeFrom('selling');
                } else {
                    this.computeFrom('margin');
                }

                var origin = [item.category, item.reference].filter(value => value).join(' – ');
                this.suggestionSource = 'Prix repris' + (origin ? ' de « ' + origin + ' »' : '')
                    + ' pour ' + (item.unit ? '1 ' + item.unit : 'une quantité de 1') + '.';
            },
            hasLotError: function(lot, codes) {
                var previewLot = this.previewLot(lot);

                return previewLot !== null && previewLot.errors.some(code => codes.indexOf(code) !== -1);
            },
            computeSelling: function() {
                if (this.marginType == 2) {
                    return this.roundCurrency((parseFloat(this.costAmount) / (100 - parseFloat(this.margin))) * 100, 2);
                } else {
                    var marginTotal = (parseFloat(this.costAmount) / 100) * parseFloat(this.margin);
                    return this.roundCurrency((parseFloat(this.costAmount) + marginTotal), 2);
                }
            },
            computeMargin: function() {
                var marginDiff = parseFloat(this.sellingAmount) - parseFloat(this.costAmount);

                if (this.marginType == 2) {
                    if (parseFloat(this.sellingAmount) != 0) {
                        var divider = parseFloat(this.sellingAmount);
                    } else {
                        return 0;
                    }
                } else {
                    if (parseFloat(this.costAmount) != 0) {
                        var divider = parseFloat(this.costAmount);
                    } else {
                        return 0;
                    }
                }

                return this.roundCurrency((marginDiff / divider) * 100, 2);
            },
            computeFrom: function(from) {
                if (!this.isNumber(this.costAmount)) {
                    return;
                }

                if (from === 'selling') {
                    if (this.isNumber(this.sellingAmount)) {
                        this.margin = this.computeMargin();
                    }
                } else if (this.isNumber(this.margin)) {
                    this.sellingAmount = this.computeSelling();
                }
            },
            sameNumber: function(left, right) {
                return this.isNumber(left) && this.isNumber(right) && parseFloat(left) === parseFloat(right);
            },
            isNumber: function(value) {
                return value !== null && value !== undefined && value !== '' && !isNaN(parseFloat(value));
            },
            amountMessages: function(field, label) {
                if (!this.isPreviewFresh) {
                    return [];
                }

                return (this.preview[field] || []).map(code => AMOUNT_MESSAGES[code] ? AMOUNT_MESSAGES[code].replace('{label}', label) : code);
            },
            formatQuantity: function(value) {
                return parseFloat(value).toLocaleString('fr-FR', {maximumFractionDigits: 3});
            },
            requestErrorMessage: function(status) {
                switch (status) {
                    case 401:
                        return 'Votre session a expiré. Rechargez la page puis réessayez.';
                    case 403:
                        return "Vous n'avez pas les droits nécessaires, ou la création de dépenses est désactivée.";
                    case 422:
                        return "Les lots ne correspondent plus à cette étude. Rechargez la page puis réessayez.";
                    case 429:
                        return 'Trop de calculs en peu de temps. Patientez quelques secondes puis réessayez.';
                    default:
                        return "L'aperçu n'a pas pu être calculé. Réessayez dans quelques instants.";
                }
            },
            recalculate: function() {
                var body = JSON.stringify(this.payload);
                var sequence = ++this.previewSequence;

                this.checked = true;
                this.loading = true;
                this.requestError = null;

                fetch(this.previewRoute, {
                    ...{
                        method: 'POST',
                        body: body,
                    },
                    ...this.getHttpJsonRequestHeaders()
                }).then(async (response) => {
                    if (sequence !== this.previewSequence) {
                        return;
                    }

                    if (response.ok) {
                        var preview = await response.json();
                        if (sequence !== this.previewSequence) {
                            return;
                        }

                        this.preview = preview;
                        this.previewSignature = body;

                        if (this.preview.valid) {
                            this.hasValidPreview = true;
                        }
                    } else {
                        this.preview = null;
                        this.previewSignature = null;
                        this.requestError = this.requestErrorMessage(response.status);
                    }
                }).catch(error => {
                    if (sequence !== this.previewSequence) {
                        return;
                    }

                    this.preview = null;
                    this.previewSignature = null;
                    this.requestError = "La répartition n'a pas pu être calculée. Vérifiez votre connexion puis réessayez.";
                }).finally(() => {
                    if (sequence === this.previewSequence) {
                        this.loading = false;
                    }
                });
            },
            generate: function() {
                if (this.saving || this.loading || this.saveBlocker !== null) {
                    return;
                }

                if (!this.labelOnlyChange) {
                    this.$emit('submit-study', {
                        operation: this.editing ? 'update' : 'create',
                        dilution_id: this.editing ? this.editing.id : null,
                        designation: this.designation.trim(),
                        amount: this.costAmount,
                        margin: this.margin,
                        selling: this.sellingAmount,
                        type: this.type,
                        type_secondary: this.effectiveTypeSecondary || null,
                        mode: this.mode,
                        lots: this.lots
                            .map((lot, position) => ({lot: lot, position: position, entry: this.entries[this.lotKey(lot)]}))
                            .filter(item => item.entry && item.entry.selected)
                            .map(item => ({position: item.position, percentage: this.mode === MODE_PERCENTAGE ? item.entry.percentage : null})),
                    });
                    this.$bvModal.hide(this.modalId);

                    return;
                }

                var body = {
                    designation: this.designation.trim(),
                    amount: this.costAmount,
                    margin: this.margin,
                    selling: this.sellingAmount,
                    lots: this.payload.lots.map(lot => ({id: lot.id, quantity: lot.quantity, unit: lot.unit})),
                };

                this.saving = true;
                this.requestError = null;

                var editing = this.editing;

                fetch(editing ? this.storeRoute + '/' + editing.id : this.storeRoute, {
                    ...{
                        method: editing ? 'PUT' : 'POST',
                        body: JSON.stringify(body),
                    },
                    ...this.getHttpJsonRequestHeaders()
                }).then(async (response) => {
                    var data = await response.json().catch(() => ({}));

                    if (response.ok) {
                        this.$emit(editing ? 'updated' : 'created', data);
                        this.reset();
                        this.$bvModal.hide(this.modalId);
                    } else {
                        this.requestError = this.generationErrorMessage(response.status, data);
                    }
                }).catch(error => {
                    this.requestError = "La dépense n'a pas pu être enregistrée. Vérifiez votre connexion puis réessayez.";
                }).finally(() => {
                    this.saving = false;
                });
            },
            generationErrorMessage: function(status, data) {
                if (status === 422) {
                    var messages = data.errors ? [].concat(...Object.values(data.errors)) : [];

                    if (messages.length === 0 && data.message) {
                        messages.push(data.message);
                    }

                    return messages.length > 0 ? [...new Set(messages)].join(' ') : "La dépense n'a pas pu être enregistrée.";
                }

                if (status === 401 || status === 403 || status === 429) {
                    return this.requestErrorMessage(status);
                }

                return "La dépense n'a pas pu être enregistrée. Réessayez dans quelques instants.";
            },
            reset: function() {
                this.designation = '';
                this.costAmount = '';
                this.margin = '';
                this.sellingAmount = '';
                this.marginInitialized = false;
                this.marginEdited = false;
                this.type = '';
                this.typeSecondary = '';
                this.mode = MODE_QUANTITY;
                this.percentagesEdited = false;
                this.entries = {};
                this.preview = null;
                this.previewSignature = null;
                this.hasValidPreview = false;
                this.requestError = null;
                this.checked = false;
                this.editing = null;
                this.pendingLotIds = null;
                this.pendingLotPositions = null;
                this.pendingPercentagesById = null;
                this.pendingPercentagesByPosition = null;
                this.suggestionSource = null;
                this.previewSequence++;
                this.loading = false;
                this.fillTarget = null;
                this.setSuspended(false);
            },
            fillLot: function(lot, field) {
                this.fillTarget = {lot: lot, field: field};
                this.setSuspended(true);
                this.$bvModal.hide(this.modalId);
            },
            onHidden: function() {
                this.visible = false;
                clearTimeout(this.previewTimer);
                this.previewTimer = null;

                if (this.fillTarget) {
                    var target = this.fillTarget;
                    this.fillTarget = null;
                    this.$emit('fill-lot', target);
                }
            },
            resume: function() {
                this.setSuspended(false);
                this.$bvModal.show(this.modalId);
            },
            setSuspended: function(value) {
                if (this.suspended !== value) {
                    this.suspended = value;
                    this.$emit('suspended-changed', value);
                }
            },
        },
    }
</script>

<style scoped>
    .od-section {
        border: 1px solid var(--ix-border);
        border-radius: 6px;
        padding: 16px 20px;
        margin-bottom: 16px;
        background: var(--ix-background);
    }

    .od-section-title {
        display: flex;
        align-items: center;
        margin-bottom: 14px;
        font-size: 0.8rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        color: var(--ix-font-color-secondary);
    }

    .od-section-number {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        margin-right: 8px;
        border-radius: 50%;
        background: var(--ix-dark);
        color: #FFF;
        font-size: 0.75rem;
        letter-spacing: 0;
    }

    .od-section-counter {
        margin-left: auto;
        font-weight: 400;
        text-transform: none;
        letter-spacing: 0;
    }

    .od-hint {
        margin-bottom: 14px;
        padding: 8px 12px;
        border: 1px solid var(--pastel-blue);
        border-radius: 4px;
        background: var(--ix-blue-light);
        font-size: 0.85rem;
        color: var(--ix-font-color);
    }

    .od-hint i {
        color: var(--ix-blue);
        margin-right: 4px;
    }

    .od-mode {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        margin-bottom: 14px;
    }

    .od-mode-card {
        position: relative;
        display: flex;
        align-items: flex-start;
        width: 100%;
        height: 100%;
        padding: 12px 40px 12px 14px;
        border: 1px solid var(--ix-border);
        border-radius: 6px;
        background: #fff;
        text-align: left;
        color: var(--ix-font-color);
        cursor: pointer;
        transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease;
    }

    .od-mode-card:hover {
        border-color: var(--ix-blue);
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
    }

    .od-mode-card:focus {
        outline: none;
    }

    .od-mode-card:focus-visible {
        box-shadow: 0 0 0 3px var(--ix-blue-light);
        border-color: var(--ix-blue);
    }

    .od-mode-card.is-active {
        border-color: var(--ix-blue);
        background: var(--ix-blue-light);
        box-shadow: inset 0 0 0 1px var(--ix-blue);
    }

    .od-mode-icon {
        display: inline-flex;
        flex-shrink: 0;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        margin-right: 12px;
        border-radius: 50%;
        background: var(--ix-background-light);
        font-size: 1rem;
        color: var(--ix-font-color-secondary);
    }

    .od-mode-card.is-active .od-mode-icon {
        background: var(--ix-blue);
        color: #fff;
    }

    .od-mode-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    .od-mode-title {
        font-size: 0.9rem;
        font-weight: 700;
    }

    .od-mode-description {
        margin-top: 2px;
        font-size: 0.8rem;
        color: var(--ix-font-color-secondary);
    }

    /* Indicateur de sélection : cercle vide, coché sur la carte active */
    .od-mode-check {
        position: absolute;
        top: 12px;
        right: 12px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border: 1px solid var(--ix-border);
        border-radius: 50%;
        background: #fff;
        font-size: 0.6rem;
        color: transparent;
    }

    .od-mode-card.is-active .od-mode-check {
        border-color: var(--ix-blue);
        background: var(--ix-blue);
        color: #fff;
    }

    @media (max-width: 575.98px) {
        .od-mode {
            grid-template-columns: 1fr;
        }
    }

    .od-designation {
        position: relative;
    }

    /* Liste créée par jQuery UI hors du composant : sélecteur profond */
    .od-designation >>> .ui-autocomplete {
        max-height: 320px;
        overflow-y: auto;
        overflow-x: hidden;
        z-index: 1060;
    }

    .od-lots .od-amount {
        font-weight: 600;
        vertical-align: middle;
    }

    .od-footer-hint {
        font-size: 0.85rem;
        color: var(--ix-font-color-secondary);
    }

    .od-percentage-total {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        margin-top: 12px;
    }

    .od-percentage-total .od-balance {
        margin-right: 12px;
    }

    .od-lots .od-lot-name {
        margin-bottom: 0;
        font-weight: 600;
        cursor: pointer;
    }

    .od-lots tr.is-selected td {
        background: var(--ix-blue-light);
    }

    .od-lots tr.is-unselected .od-lot-name {
        font-weight: 400;
        color: var(--ix-font-color-secondary);
    }

    .od-lots tr.od-lot-details td {
        padding-top: 0;
        border-top: none;
    }

    .od-tag {
        display: inline-block;
        margin-left: 6px;
        padding: 1px 6px;
        border: 1px solid var(--ix-border);
        border-radius: 3px;
        background: var(--ix-background-light);
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        color: var(--ix-font-color-secondary);
    }

    .od-lots .input-group .form-control {
        min-width: 56px;
    }

    .od-complete {
        border: 1px solid var(--ix-border);
        background: #fff;
        font-size: 0.75rem;
        font-weight: 600;
        white-space: nowrap;
        color: var(--ix-blue);
    }

    .od-complete:hover,
    .od-complete:focus {
        background: var(--ix-blue-light);
        color: var(--ix-blue);
    }

    .od-total td {
        font-weight: 700;
        border-top: 2px solid var(--ix-border);
    }

    .od-balance {
        padding: 8px 12px;
        border-radius: 4px;
        font-weight: 600;
    }

    .od-balance.is-balanced {
        background: var(--ix-green-light);
        color: var(--ix-green-shaded);
    }

    .od-balance.is-unbalanced {
        background: var(--ix-red-light);
        color: var(--ix-red-shaded);
    }
</style>
